import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Intent } from '@blueprintjs/core';
import { Breadcrumbs } from 'components/navigation';
import { Collapsable, ShowHide } from 'components/layout';
import { ListingTable } from 'components/listing';
import { ActionPanel } from 'components/panel';
import { StatusBadge } from 'components/status';
import { Button } from 'components/buttons';
import { DateToLocal } from 'components/dates';
import { NotificationInline, NotificationToaster } from 'components/notifications';
import { UserAccess } from 'components/user-access';
import { SystemMonitoringService } from 'services';
import formatMetricWithThreshold from 'utils/formatMetricWithThreshold';
import formatArrivalsOverWindow from 'utils/formatArrivalsOverWindow';

const defaultArray = [];

const requiredAction = 'Internal:DevTeam';

const unhealthySummaryHeaders = ['Microservice', 'Service', 'Type', 'Reason', 'Unhealthy since', ''];

const queueHeaders = ['Queue', 'Items (threshold)', 'Arrivals (min expected)', 'Oldest item', 'Status'];

const syncHeaders = ['Oldest table sync', 'Status'];

// Timing and offsets for the "jump to section" scroll, named to avoid magic numbers.
const SECTION_EXPAND_DELAY_MS = 250;
const SCROLL_ANIMATION_MS = 400;
const FIXED_NAV_HEIGHT_PX = 60;
const SCROLL_TOP_MARGIN_PX = 16;
const SECTION_SCROLL_OFFSET_PX = FIXED_NAV_HEIGHT_PX + SCROLL_TOP_MARGIN_PX;
const EASE_MIDPOINT = 0.5;
const EASE_QUAD_FACTOR = 2;

export function SystemMonitoring({ breadcrumbs = defaultArray }) {
    const [microservices, setMicroservices] = useState(defaultArray);
    const [isLoading, setIsLoading] = useState(true);
    const [expandedMicroservices, setExpandedMicroservices] = useState({});

    // Maps the raw serviceType discriminator to the label shown to the user.
    function getServiceTypeLabel(serviceType) {
        if (serviceType === 'functionApp') {
            return 'Function App';
        }

        if (serviceType === 'webJob') {
            return 'WebJob';
        }

        return serviceType;
    }

    // Distinct icons per service type: a function app is event-triggered (lightning), a webjob is a
    // continuously running background worker (cog).
    function getServiceTypeIcon(serviceType) {
        if (serviceType === 'functionApp') {
            return 'lightning';
        }

        if (serviceType === 'webJob') {
            return 'cog';
        }

        return 'application';
    }

    // Stable DOM id for a microservice section so the summary table can scroll it into view.
    function microserviceElementId(microserviceName) {
        return `microservice-${microserviceName}`;
    }

    // Nearest scrollable ancestor of an element. The app scrolls within an inner container rather than the
    // window, so smooth scrolling has to target that container directly.
    function getScrollParent(node) {
        let current = node.parentElement;
        while (current != null) {
            const overflowY = window.getComputedStyle(current).overflowY;
            if ((overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight) {
                return current;
            }
            current = current.parentElement;
        }
        return null;
    }

    // Standard ease-in-out-quad: accelerates to the midpoint then decelerates. Input and output are 0..1.
    function easeInOutQuad(progress) {
        if (progress < EASE_MIDPOINT) {
            return EASE_QUAD_FACTOR * progress * progress;
        }

        const shifted = (-EASE_QUAD_FACTOR * progress) + EASE_QUAD_FACTOR;
        return 1 - ((shifted * shifted) / EASE_QUAD_FACTOR);
    }

    // Animate a container's scrollTop to a target over the given duration. The app's scroll container does
    // not honour the native smooth-scroll behaviour, so we drive the animation ourselves with an ease-in-out.
    function smoothScrollTo(container, target, duration) {
        const start = container.scrollTop;
        const distance = target - start;
        const startTime = performance.now();

        function step(now) {
            const elapsed = now - startTime;
            const progress = elapsed >= duration ? 1 : elapsed / duration;
            container.scrollTop = start + (distance * easeInOutQuad(progress));
            if (progress < 1) {
                window.requestAnimationFrame(step);
            }
        }

        window.requestAnimationFrame(step);
    }

    // A service is considered unhealthy if it is itself unhealthy or any of its children are.
    function isServiceUnhealthy(service) {
        return service.isHealthy === false || service.isAnyChildUnhealthy === true;
    }

    // Mirrors BackgroundHealthCheckService.ComputeQueueHealth in the System microservice: a queue is quiet when
    // too little work arrived over a window the reporter actually measured. Every condition the backend gates
    // on is repeated here, including that the window is present and positive — a count measured over no stated
    // period, or over no elapsed time, is not evidence the producer stopped. Dropping either would let this
    // name a queue "not receiving work" that the backend judged unhealthy for some other reason entirely, and
    // send whoever is on call after a producer that is fine. It only picks wording for a queue the backend has
    // already reported unhealthy.
    function isQueueQuiet(queue) {
        return queue.arrivalsInQuietWindow != null
            && queue.quietWindowMinutes != null
            && queue.quietWindowMinutes > 0
            && queue.minimumExpectedArrivals != null
            && queue.arrivalsInQuietWindow < queue.minimumExpectedArrivals;
    }

    // A very brief, human-readable reason a service is unhealthy, for the summary table. Staleness is
    // reported first; otherwise the named issues, then queues that have gone quiet, then queues unhealthy
    // for any other reason, then an unhealthy sync.
    function getUnhealthyReason(service) {
        if (service.isHealthy === false && service.isAnyChildUnhealthy === false) {
            return 'Not heard from';
        }

        const reasons = [];

        const issues = service.issues == null ? defaultArray : service.issues;
        issues.forEach((issue) => {
            reasons.push(issue.friendlyName);
        });

        const queues = service.queues == null ? defaultArray : service.queues;
        const unhealthyQueues = queues.filter((queue) => {
            return queue.isHealthy === false;
        });
        const quietQueueCount = unhealthyQueues.filter(isQueueQuiet).length;
        const otherQueueCount = unhealthyQueues.length - quietQueueCount;

        if (quietQueueCount === 1) {
            reasons.push('Queue not receiving work');
        } else if (quietQueueCount > 1) {
            reasons.push(`${quietQueueCount} queues not receiving work`);
        }

        if (otherQueueCount === 1) {
            reasons.push('Queue unhealthy');
        } else if (otherQueueCount > 1) {
            reasons.push(`${otherQueueCount} queues unhealthy`);
        }

        if (service.syncStatus != null && service.syncStatus.isHealthy === false) {
            reasons.push('Sync unhealthy');
        }

        return reasons.join(', ');
    }

    // Orders unhealthy-service entries with the longest-unhealthy (oldest start time) first. Entries with no
    // recorded unhealthy start time sort last.
    function compareByLongestUnhealthy(first, second) {
        const firstStart = first.service.lastUnhealthyStartTime;
        const secondStart = second.service.lastUnhealthyStartTime;

        if (firstStart == null && secondStart == null) {
            return 0;
        }
        if (firstStart == null) {
            return 1;
        }
        if (secondStart == null) {
            return -1;
        }

        return new Date(firstStart).getTime() - new Date(secondStart).getTime();
    }

    // Orders services so unhealthy ones surface first, then alphabetically by friendly name.
    function sortServices(services) {
        return [...services].sort((first, second) => {
            const firstUnhealthy = isServiceUnhealthy(first);
            const secondUnhealthy = isServiceUnhealthy(second);

            if (firstUnhealthy !== secondUnhealthy) {
                return firstUnhealthy ? -1 : 1;
            }

            return first.friendlyName.localeCompare(second.friendlyName);
        });
    }

    // Counts the unhealthy services within a single microservice.
    function countUnhealthyServices(microservice) {
        return microservice.services.filter(isServiceUnhealthy).length;
    }

    // Orders microservices so those containing unhealthy services surface first, then alphabetically by name.
    function sortMicroservices(list) {
        return [...list].sort((first, second) => {
            const firstUnhealthyCount = countUnhealthyServices(first);
            const secondUnhealthyCount = countUnhealthyServices(second);

            if ((firstUnhealthyCount > 0) !== (secondUnhealthyCount > 0)) {
                return firstUnhealthyCount > 0 ? -1 : 1;
            }

            return first.microserviceName.localeCompare(second.microserviceName);
        });
    }

    function renderHealthBadge(healthy) {
        if (healthy) {
            return (
                <StatusBadge text="Healthy" iconName="tick-circle" intent={Intent.SUCCESS} />
            );
        }

        return (
            <StatusBadge text="Unhealthy" iconName="error" intent={Intent.DANGER} />
        );
    }

    // Queue health names the quiet case explicitly so a queue that has stopped receiving work is not
    // mistaken for a backlog. It stays at danger severity — a silent queue is as serious as a full one.
    function renderQueueHealthBadge(queue) {
        if (queue.isHealthy) {
            return (
                <StatusBadge text="Healthy" iconName="tick-circle" intent={Intent.SUCCESS} />
            );
        }

        if (isQueueQuiet(queue)) {
            return (
                <StatusBadge text="Not receiving work" iconName="error" intent={Intent.DANGER} />
            );
        }

        return (
            <StatusBadge text="Unhealthy" iconName="error" intent={Intent.DANGER} />
        );
    }

    function renderServiceTypeBadge(serviceType) {
        return (
            <StatusBadge text={getServiceTypeLabel(serviceType)} iconName={getServiceTypeIcon(serviceType)} intent={Intent.NONE} />
        );
    }

    // Indicator shown when a service's resource group was deployed within the health-suppression window.
    // It signals that any brief unhealthy state is likely a side effect of the deploy rather than a real
    // fault. The tooltip shows when the deploy was detected and notes that health status is not
    // updated mid-deploy — it settles once the deployment window passes.
    function renderRecentlyDeployedBadge(service, text) {
        return (
            <StatusBadge
                text={text}
                iconName="cloud-upload"
                intent={Intent.PRIMARY}
                showTooltip
                tooltipContent={(<Fragment>Recently deployed <DateToLocal>{service.deployDetectedAt}</DateToLocal>. Health status isn&apos;t updated until the deployment has settled.</Fragment>)}
            />
        );
    }

    useEffect(() => {
        SystemMonitoringService.getHealthChecks().then(
            (result) => {
                const data = result == null ? defaultArray : result;
                setMicroservices(data);

                // Default each microservice to expanded when it contains an unhealthy service, collapsed otherwise.
                const initialExpanded = {};
                data.forEach((microservice) => {
                    initialExpanded[microservice.microserviceName] = countUnhealthyServices(microservice) > 0;
                });
                setExpandedMicroservices(initialExpanded);

                setIsLoading(false);
            },
            () => {
                NotificationToaster.show(Intent.DANGER, 'Could not load system monitoring data');
                setIsLoading(false);
            }
        );
    }, []);

    function toggleMicroservice(microserviceName) {
        setExpandedMicroservices((current) => {
            return {
                ...current,
                [microserviceName]: !current[microserviceName]
            };
        });
    }

    // Expand the owning microservice section (if collapsed) and smoothly scroll its header into view.
    function jumpToMicroservice(microserviceName) {
        setExpandedMicroservices((current) => {
            return {
                ...current,
                [microserviceName]: true
            };
        });

        // Defer the scroll until the expanded section has finished its open animation and laid out.
        window.setTimeout(() => {
            const element = document.getElementById(microserviceElementId(microserviceName));
            if (element == null) {
                return;
            }

            const container = getScrollParent(element);
            if (container == null) {
                element.scrollIntoView({ block: 'start' });
                return;
            }

            // Land the section header just below the fixed navigation bar so it isn't hidden behind it.
            // element.getBoundingClientRect().top is viewport-relative, so the scroll delta to bring the
            // header to the offset is (rect top - offset). The browser clamps if the target exceeds range.
            const target = container.scrollTop + element.getBoundingClientRect().top - SECTION_SCROLL_OFFSET_PX;
            smoothScrollTo(container, target, SCROLL_ANIMATION_MS);
        }, SECTION_EXPAND_DELAY_MS);
    }

    // Every unhealthy service across all microservices, ordered longest-unhealthy first, as rows for the
    // summary table. Each row carries a jump arrow that opens and scrolls to its section below.
    function buildUnhealthySummaryRows() {
        const unhealthy = [];

        microservices.forEach((microservice) => {
            microservice.services.forEach((service) => {
                if (isServiceUnhealthy(service)) {
                    unhealthy.push({ microservice, service });
                }
            });
        });

        unhealthy.sort(compareByLongestUnhealthy);

        return unhealthy.map((entry) => {
            const microservice = entry.microservice;
            const service = entry.service;

            return [
                microservice.microserviceName,
                service.friendlyName,
                renderServiceTypeBadge(service.serviceType),
                getUnhealthyReason(service),
                <DateToLocal key={`${service.key}-since`}>{service.lastUnhealthyStartTime}</DateToLocal>,
                <Button
                    key={`${service.key}-actions`}
                    icon="arrow-right"
                    iconOnly
                    minimal
                    small
                    onClick={() => jumpToMicroservice(microservice.microserviceName)}
                />
            ];
        });
    }

    function renderQueues(service) {
        if (service.queues == null || service.queues.length === 0) {
            return null;
        }

        const rows = service.queues.map((queue) => {
            return [
                queue.friendlyName,
                formatMetricWithThreshold(queue.numberOfItemsInQueue, queue.numberOfItemsUnhealthyThreshold),
                formatArrivalsOverWindow(queue.arrivalsInQuietWindow, queue.minimumExpectedArrivals, queue.quietWindowMinutes),
                <DateToLocal key={`${queue.key}-oldest`}>{queue.oldestItemQueueTime}</DateToLocal>,
                <span key={`${queue.key}-status`}>{renderQueueHealthBadge(queue)}</span>
            ];
        });

        return (
            <div className="spacer-top-small">
                <h5>Queues</h5>
                <ListingTable
                    headers={queueHeaders}
                    data={rows}
                    loadingData={false}
                    totalRecordCount={rows.length}
                    sortable={false}
                    pageable={false}
                />
            </div>
        );
    }

    function renderSyncStatus(service) {
        if (service.syncStatus == null) {
            return null;
        }

        const rows = [
            [
                <DateToLocal key={`${service.key}-sync-oldest`}>{service.syncStatus.oldestTableSyncTime}</DateToLocal>,
                <span key={`${service.key}-sync-status`}>{renderHealthBadge(service.syncStatus.isHealthy)}</span>
            ]
        ];

        return (
            <div className="spacer-top-small">
                <h5>Sync status</h5>
                <ListingTable
                    headers={syncHeaders}
                    data={rows}
                    loadingData={false}
                    totalRecordCount={rows.length}
                    sortable={false}
                    pageable={false}
                />
            </div>
        );
    }

    function renderIssues(service) {
        if (service.issues == null || service.issues.length === 0) {
            return null;
        }

        return (
            <div className="spacer-top-small">
                <h5>Issues</h5>
                {
                    service.issues.map((issue) => {
                        return (
                            <NotificationInline
                                key={`${service.key}-issue-${issue.key}`}
                                intent="danger"
                                show
                                allowClose={false}
                                text={(
                                    <Fragment>
                                        <div><strong>{issue.friendlyName}</strong></div>
                                        <div>{issue.description}</div>
                                        <div><strong>Resolution: </strong>{issue.resolution}</div>
                                    </Fragment>
                                )}
                            />
                        );
                    })
                }
            </div>
        );
    }

    function renderService(microservice, service) {
        const unhealthy = isServiceUnhealthy(service);
        // Unhealthy with no failing queue, sync or issue means the service simply went quiet — the liveness
        // check flipped it unhealthy because we have not heard from it. Call that out explicitly in red.
        const notHeardFrom = service.isHealthy === false && service.isAnyChildUnhealthy === false;

        return (
            <div className="spacer-top-small" key={`${microservice.microserviceName}-${service.key}`}>
                <ActionPanel
                    intent={unhealthy ? 'danger' : 'success'}
                    title={service.friendlyName}
                >
                    <div className="inline-items">
                        {renderServiceTypeBadge(service.serviceType)}
                        {renderHealthBadge(!unhealthy)}
                        <div className="inline-item fill"></div>
                        <ShowHide
                            evaluator={notHeardFrom}
                            show={(
                                <StatusBadge
                                    iconName="error"
                                    intent={Intent.DANGER}
                                    text={(<Fragment>Not heard from since <DateToLocal>{service.lastHeardFrom}</DateToLocal></Fragment>)}
                                />
                            )}
                            hide={(
                                <span className="bp6-text-muted">Last heard from <DateToLocal>{service.lastHeardFrom}</DateToLocal></span>
                            )}
                        />
                        <ShowHide
                            evaluator={unhealthy && !notHeardFrom}
                            show={(
                                <span className="bp6-text-muted">Unhealthy since <DateToLocal>{service.lastUnhealthyStartTime}</DateToLocal></span>
                            )}
                        />
                    </div>

                    {renderQueues(service)}
                    {renderSyncStatus(service)}
                    {renderIssues(service)}
                </ActionPanel>
            </div>
        );
    }

    function renderMicroservice(microservice) {
        const unhealthyCount = countUnhealthyServices(microservice);
        const sortedServices = sortServices(microservice.services);

        // Surface the recently-deployed indicator at the microservice level too: if any child service was
        // deployed within the window, badge the whole group so a collapsed microservice still signals that a
        // brief unhealthy state inside it is likely deploy-related. A deploy stamps every service in a
        // resource group together, so any recently-deployed child carries a representative deploy time.
        const deployedChild = microservice.services.find((service) => {
            return service.recentlyDeployed === true;
        });

        let recentlyDeployedBadge = null;
        if (deployedChild != null) {
            recentlyDeployedBadge = renderRecentlyDeployedBadge(deployedChild, 'Recently deployed');
        }

        const metadata = (
            <div className="inline-items">
                <span className="inline-item">
                    <ShowHide
                        evaluator={unhealthyCount > 0}
                        show={(
                            <StatusBadge text={`${unhealthyCount} unhealthy`} iconName="error" intent={Intent.DANGER} />
                        )}
                        hide={(
                            <StatusBadge text="All healthy" iconName="tick-circle" intent={Intent.SUCCESS} />
                        )}
                    />
                </span>
                <ShowHide
                    evaluator={deployedChild != null}
                    show={recentlyDeployedBadge}
                />
            </div>
        );

        return (
            <div className="spacer-top-small" id={microserviceElementId(microservice.microserviceName)} key={microservice.microserviceName}>
                <Collapsable
                    title={microservice.microserviceName}
                    metadata={metadata}
                    fullWidth
                    expanded={expandedMicroservices[microservice.microserviceName] === true}
                    onClick={() => toggleMicroservice(microservice.microserviceName)}
                >
                    {
                        sortedServices.map((service) => {
                            return renderService(microservice, service);
                        })
                    }
                </Collapsable>
            </div>
        );
    }

    const unhealthyRows = buildUnhealthySummaryRows();
    const sortedMicroservices = sortMicroservices(microservices);
    const hasData = microservices.length > 0;

    return (
        <UserAccess
            perform={requiredAction}
            yes={() => (
                <div className="row">

                    <Breadcrumbs items={breadcrumbs} />

                    <h1>System Monitoring</h1>
                    <div className="intro-text">
                        <p>From here you can monitor the health of platform microservices and their underlying services.</p>
                    </div>

                    <ShowHide
                        evaluator={!isLoading && !hasData}
                        show={(
                            <NotificationInline intent="info" show allowClose={false} text="No services reporting." />
                        )}
                        hide={(
                            <Fragment>
                                <div className="spacer-top-small">
                                    <h2>Unhealthy services</h2>
                                    <ListingTable
                                        headers={unhealthySummaryHeaders}
                                        data={unhealthyRows}
                                        loadingData={isLoading}
                                        totalRecordCount={unhealthyRows.length}
                                        sortable={false}
                                        pageable={false}
                                        noDataMessage="All services are healthy."
                                    />
                                </div>

                                <div className="spacer-top-small">
                                    <h2>All Services</h2>
                                    {
                                        sortedMicroservices.map((microservice) => {
                                            return renderMicroservice(microservice);
                                        })
                                    }
                                </div>
                            </Fragment>
                        )}
                    />
                </div>
            )}
        />
    );
}

SystemMonitoring.propTypes = {
    breadcrumbs: PropTypes.array
};
